// import * as Next from 'next'
import * as React from 'react'
// import { useState } from 'react'
import { ParsedUrlQuery } from 'querystring'
import Head from 'next/head'
// import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import Image from 'next/image'
import Script from 'next/script'
import Link from 'next/link'
import {
	Alert,
	Badge,
	Box,
	Button,
	Divider,
	Paper,
	Popover,
	Text,
	Title,
	Image as MantineImage,
	Table,
	Rating,
	Tooltip,
	Pagination,
	Select,
	TextInput,
	Spoiler,
	Skeleton,
	Blockquote,
	Chip,
	Group,
	Stack,
	Center,
	Tabs,
} from '@mantine/core'
import { Carousel } from '@mantine/carousel'
import {
	IconBrandInstagram,
	IconBrandFacebook,
	IconBrandTiktok,
	IconCertificate,
	IconMail,
	IconMapPin,
	IconExternalLink,
	IconPigMoney,
	IconPhone,
	IconWorldWww,
	IconBrandYoutube,
	IconBrandWhatsapp,
	IconStar,
} from '@tabler/icons-react'
import pluralize from 'pluralize'
import * as Types from '../../declarations'
import { ACCENT_COLOUR } from '../../pages/_app'
import * as RedisUtil from 'lib/redis'
import * as ReviewsUtil from 'lib/reviews'
import {
	ensureHttpUrl,
	formatDifficulty,
	getCountryRoute,
	getGuideProfileUrl,
	getTrekRoute,
	getUploadThumbPath,
} from 'lib/display'
import {
	extractYoutubeId,
	getDefaultImage,
	getFlagURL,
	sluggify,
	tripPageTripToTripCard,
	capitalizeWords,
} from 'lib/helper'
import * as AnalyitcsUtil from 'lib/analytics'
import { useQuery } from '@apollo/client'
import { useAppDispatch, useAppSelector } from './../../state/app-store'
import * as GQL from 'lib/queries'

import PageTemplate from '../../components/PageTemplate'
import TrekCard from 'components/TrekCard'
import CountryBadge from 'components/CountryBadge'

import { withVersion } from '../../lib/withVersion'
import TripAdvisorWidget from 'components/guide/TripAdvisorWidget'
import WhatsAppContact from 'components/guide/WhatsAppContact'
import Video from 'components/Video'
import YouTubeChannel from 'components/YoutubeChannel'
import FormatPrice from 'components/FormatPrice'

import JustifiedGallery from 'components/TrekPage/components/JustifiedGallery'
import ImageModal from 'components/TrekPage/components/ImageModal'
import EmailAddress from 'components/EmailAddress'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)
import * as Selectors from './../../state/selectors'
import SignUpNudge from 'components/SignUpNudge'

interface GuidePageProps {
	guide: Types.API.GuideCompany
	version: Types.VERSION
	// first reviews page, fetched in gSSP so crawlers see review content
	initialReviews?: Types.API.Reviews.GetGuideReviews.Response
}

const formatTripNames = (names: string[] | undefined) => {
	if (!names || names.length === 0) return ''
	if (names.length === 1) return names[0]
	if (names.length === 2) return names.join(' and ')
	return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

const providerStyles = {
	google: {
		color: '#4285F4',
		label: 'Google',
		ratingColor: 'blue',
	},
	tripadvisor: {
		color: '#00AA6C',
		label: 'TripAdvisor',
		ratingColor: 'green',
	},
	trustpilot: {
		color: '#00B67A',
		label: 'TrustPilot',
		ratingColor: 'teal',
	},
} as const

const highlightSearchTerms = (text: string, searchTerm: string) => {
	if (!searchTerm) return text

	const regex = new RegExp(`(${searchTerm})`, 'gi')
	return text.split(regex).map((part, i) =>
		regex.test(part) ? (
			<strong key={i} style={{ textDecoration: 'underline' }}>
				{part}
			</strong>
		) : (
			part
		),
	)
}
const ReviewsSection = ({
	guide,
	initialReviews,
}: {
	guide: Types.API.GuideCompany
	initialReviews?: Types.API.Reviews.GetGuideReviews.Response
}) => {
	const [page, setPage] = React.useState(1)
	const [
		provider,
		setProvider,
	] = React.useState<Types.App.DataTypes.Reviews.Provider | null>(null)
	const [keyword, setKeyword] = React.useState('')
	const [debouncedKeyword, setDebouncedKeyword] = React.useState('')
	const limit = 10
	const reviewsRef = React.useRef<HTMLDivElement>(null)

	React.useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedKeyword(keyword)
			setPage(1)
		}, 300)

		return () => clearTimeout(timer)
	}, [keyword])

	const { data, loading } = useQuery<
		{ getGuideReviews: Types.API.Reviews.GetGuideReviews.Response },
		{ input: Types.API.Reviews.GetGuideReviews.Input }
	>(GQL.Queries.GET_GUIDE_REVIEWS, {
		variables: {
			input: {
				guideId: guide.id,
				page,
				limit,
				provider: provider ? provider : null,
				keyword: debouncedKeyword || undefined,
			},
		},
		fetchPolicy: 'network-only',
	})

	// server-rendered first page; the live query takes over once it lands
	const reviewsData = data?.getGuideReviews ?? initialReviews

	const authedUser = useAppSelector(Selectors.authedUser)
	const isAuthedUser = !!authedUser

	const handlePageChange = (newPage: number) => {
		setPage(newPage)
		reviewsRef.current?.scrollIntoView({ behavior: 'smooth' })
	}

	if (!guide.companyReviewData?.totalReviewCount) {
		return <Text>This guide doesn&apos;t have any reviews yet.</Text>
	}

	return (
		<div ref={reviewsRef}>
			<div style={{ position: 'relative' }}>
				<Title order={3} mt={'lg'} mb={'lg'}>
					All reviews of {guide.companyName}
				</Title>
				<div
					style={{
						opacity: isAuthedUser ? 1 : 0.4,
						transition: 'all 0.5s ease',
						filter: isAuthedUser ? 'none' : 'blur(2px)',
						pointerEvents: isAuthedUser ? 'auto' : 'none',
						height: isAuthedUser ? 'auto' : '400px',
						overflow: 'hidden',
					}}
				>
					<div
						style={{
							display: 'flex',
							gap: '1rem',
							marginBottom: '1rem',
						}}
					>
						<Select
							label="Filter by platform"
							placeholder="All platforms"
							value={provider}
							onChange={(val) =>
								setProvider(val as Types.App.DataTypes.Reviews.Provider | null)
							}
							data={[
								{ value: '', label: 'All platforms' },
								{
									value: 'google',
									label: `Google (${reviewsData?.providerCounts.google ?? 0})`,
									disabled: !reviewsData?.providerCounts.google,
								},
								{
									value: 'tripadvisor',
									label: `TripAdvisor (${
										reviewsData?.providerCounts.tripadvisor ?? 0
									})`,
									disabled: !reviewsData?.providerCounts.tripadvisor,
								},
								{
									value: 'trustpilot',
									label: `TrustPilot (${
										reviewsData?.providerCounts.trustpilot ?? 0
									})`,
									disabled: !reviewsData?.providerCounts.trustpilot,
								},
							]}
							style={{ width: '140px' }}
						/>
						<TextInput
							label="Search reviews"
							placeholder="Search..."
							value={keyword}
							onChange={(e) => setKeyword(e.currentTarget.value)}
							rightSection={
								keyword && (
									<Text
										color="dimmed"
										style={{ cursor: 'pointer' }}
										onClick={() => setKeyword('')}
									>
										✕
									</Text>
								)
							}
							style={{ flex: 1 }}
						/>
					</div>

					{(!!reviewsData || !loading) &&
						reviewsData?.keywordCounts &&
						reviewsData?.keywordCounts.length > 0 && (
							<div style={{ marginBottom: '1rem' }}>
								{['peak', 'term'].map((type) => {
									const keywords = reviewsData?.keywordCounts.filter(
										(kw) => kw.type === type,
									)
									return keywords?.length ? (
										<div
											key={type}
											style={{ marginBottom: type === 'peak' ? '1rem' : 0 }}
										>
											<Text size="sm" fw={500} mb="xs">
												{type === 'peak'
													? 'Filter by Trip / Mountain:'
													: 'Filter by keyword:'}
											</Text>
											<div
												style={{
													display: 'flex',
													gap: '0.5rem',
													flexWrap: 'wrap',
												}}
											>
												{keywords
													.sort((a, b) => b.count - a.count)
													.map((kw) => (
														<Chip
															key={kw.keyword}
															checked={debouncedKeyword === kw.keyword}
															onChange={() =>
																setKeyword(
																	keyword === kw.keyword ? '' : kw.keyword,
																)
															}
															variant={
																kw.type === 'peak'
																	? debouncedKeyword === kw.keyword
																		? 'filled'
																		: 'light'
																	: 'outline'
															}
														>
															{kw.keyword} ({kw.count})
														</Chip>
													))}
											</div>
										</div>
									) : null
								})}
							</div>
						)}

					{loading ? (
						<>
							<Skeleton height={120} radius="md" mb="md" />
							<Skeleton height={120} radius="md" mb="md" />
							<Skeleton height={120} radius="md" mb="md" />
						</>
					) : (
						<>
							<Text size="sm" color="dimmed" mb="md">
								{provider || keyword ? (
									reviewsData?.reviews.length === 0 ? (
										<>
											No reviews found
											{provider && ` from ${providerStyles[provider].label}`}
											{keyword && ` containing "${keyword}"`}
										</>
									) : (
										<>
											Showing {reviewsData?.reviews.length.toLocaleString()} of{' '}
											{reviewsData?.totalCount.toLocaleString()} reviews
											{provider && ` from ${providerStyles[provider].label}`}
											{keyword && ` containing "${keyword}"`}
										</>
									)
								) : (
									`${reviewsData?.totalCount.toLocaleString()} reviews`
								)}
							</Text>
							{reviewsData?.reviews.map((review) => {
								const reviewData = JSON.parse(review.jsonReviewBlob)
								const reviewText = reviewData.review_text || reviewData.text
								const reviewTitle = reviewData.title || reviewData.review_title
								const reviewDate =
									reviewData.timestamp || reviewData.review_date
								const formattedDate = dayjs(reviewDate).format('MMMM Do, YYYY')
								const providerStyle = providerStyles[review.provider]
								const reviewUrl = reviewData.url || reviewData.review_url

								return (
									<Paper
										key={review.id}
										p="md"
										withBorder
										mb="md"
										style={{
											borderLeft: `3px solid ${providerStyle.color}`,
											borderTop: `1px solid ${providerStyle.color}33`,
											borderRight: `1px solid ${providerStyle.color}33`,
											borderBottom: `1px solid ${providerStyle.color}33`,
										}}
									>
										<div
											style={{
												display: 'flex',
												justifyContent: 'space-between',
												alignItems: 'flex-start',
												marginBottom: '8px',
											}}
										>
											<div>
												{reviewTitle && (
													<Text fw={600}>
														{debouncedKeyword
															? highlightSearchTerms(
																	reviewTitle,
																	debouncedKeyword,
															  )
															: reviewTitle}
													</Text>
												)}
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '8px',
														marginBottom: '12px',
													}}
												>
													<Text size="sm" color="dimmed">
														{formattedDate}
													</Text>
													<Badge
														size="sm"
														color={providerStyle.ratingColor}
														variant="outline"
													>
														{providerStyle.label}
													</Badge>
												</div>
											</div>

											<div style={{ textAlign: 'right' }}>
												<Rating
													value={review.reviewPercent / 20}
													readOnly
													fractions={10}
													color={providerStyle.ratingColor}
												/>
												{reviewUrl && (
													<Text
														size="xs"
														color="dimmed"
														component="a"
														href={reviewUrl}
														target="_blank"
														rel="noopener noreferrer"
														mt={4}
														style={{
															display: 'flex',
															alignItems: 'center',
															gap: '4px',
															justifyContent: 'flex-end',
														}}
													>
														View original
														<IconExternalLink size={12} />
													</Text>
												)}
											</div>
										</div>

										{!!reviewText && (
											<Spoiler
												// show the full review if the user entered a search term
												maxHeight={debouncedKeyword ? 10000 : 100}
												showLabel="Show full review..."
												hideLabel="Show less"
												mt="xs"
											>
												{debouncedKeyword
													? highlightSearchTerms(reviewText, debouncedKeyword)
													: reviewText}
											</Spoiler>
										)}
										{!reviewText && (
											<Text mt="xs" color="dimmed">
												This review has no text - the reviewer just gave a
												rating only.
											</Text>
										)}
										{reviewData.owner_answer && (
											<div style={{ marginTop: '1rem' }}>
												<Text size="sm" fw={500} mb={6}>
													{guide.companyName} responded:
												</Text>
												<Blockquote
													color="gray"
													icon={null}
													styles={{
														root: {
															padding: '0.5rem 1rem',
															fontSize: '0.875rem',
															fontStyle: 'italic',
														},
													}}
												>
													{reviewData.owner_answer}
												</Blockquote>
											</div>
										)}
									</Paper>
								)
							})}
						</>
					)}

					{(!!reviewsData || !loading) &&
						(reviewsData?.totalCount ?? 0) > limit && (
							<div
								style={{
									display: 'flex',
									justifyContent: 'center',
									marginTop: '1rem',
								}}
							>
								<Pagination
									total={Math.ceil((reviewsData?.totalCount ?? 0) / limit)}
									value={page}
									onChange={handlePageChange}
									// Shared pagination skin (app/style.scss)
									classNames={{ root: 'gt-pagination' }}
								/>
							</div>
						)}
				</div>
				{!isAuthedUser && (
					<SignUpNudge
						context={{
							variant: 'REVIEWS',

							contextMessage: guide.slug,
							guideName: guide.companyName,
							reviewCount: reviewsData?.totalCount ?? 0,
						}}
					/>
				)}
			</div>
		</div>
	)
}

interface ReviewRowProps {
	name: string
	url?: string
	average: number
	count: number
	color?: string
	isInternalScore?: boolean
}

const ReviewRow = ({
	name,
	url,
	average,
	count,
	// color = 'yellow',
	isInternalScore,
}: ReviewRowProps) => {
	const score = average / 20
	const formattedScore = score.toFixed(1).replace(/\.0$/, '')

	return (
		<tr>
			<td style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
				{url ? (
					<a
						href={url}
						target="_blank"
						rel="noopener noreferrer"
						style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
					>
						{isInternalScore ? <strong>{name}</strong> : name}
						<IconExternalLink size={14} />
					</a>
				) : (
					<strong>{name}</strong>
				)}
			</td>
			<td>
				<Tooltip label={`Rated ${formattedScore} on ${name}`}>
					<div>
						<Rating
							value={score}
							readOnly
							fractions={10}
							color={isInternalScore ? 'default' : 'yellow'}
						/>
					</div>
				</Tooltip>
			</td>
			<td style={{ textAlign: 'right', paddingRight: '16px' }}>
				{formattedScore} / 5
			</td>
			<td style={{ textAlign: 'right' }}>{count}</td>
		</tr>
	)
}

const TopReviews = ({
	guide,
	initialReviews,
}: {
	guide: Types.API.GuideCompany
	initialReviews?: Types.API.Reviews.GetGuideReviews.Response
}) => {
	const { data } = useQuery<
		{ getGuideReviews: Types.API.Reviews.GetGuideReviews.Response },
		{ input: Types.API.Reviews.GetGuideReviews.Input }
	>(GQL.Queries.GET_GUIDE_REVIEWS, {
		variables: {
			input: {
				guideId: guide.id,
				page: 1,
				limit: 5,
				provider: null,
			},
		},
		fetchPolicy: 'network-only',
	})

	// server-rendered first page; the live query takes over once it lands
	const reviewsData = data?.getGuideReviews ?? initialReviews
	if (!reviewsData?.reviews) {
		return null
	}

	const reviews = reviewsData.reviews
		.map((review) => {
			const reviewData = JSON.parse(review.jsonReviewBlob)
			return {
				provider: review.provider,
				score: review.reviewPercent,
				text: reviewData.review_text || reviewData.text,
				title: reviewData.title || reviewData.review_title,
				date: reviewData.timestamp || reviewData.review_date,
			}
		})
		// Filter out reviews with no content (no text and no title)
		.filter((review) => review.text || review.title)
		.sort((a, b) => b.score - a.score)
		.slice(0, 5)

	return (
		<Carousel
			withIndicators
			height={200}
			slideSize="100%"
			slideGap="md"
			emblaOptions={{ loop: true, align: 'start' }}
		>
			{reviews.map((review, index) => (
				<Carousel.Slide key={index}>
					<Paper
						p="md"
						withBorder
						style={{ height: '100%', paddingLeft: 48, paddingRight: 48 }}
					>
						<div
							style={{
								display: 'flex',
								flexDirection: 'column',
								height: '100%',
							}}
						>
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: '12px',
									marginBottom: '8px',
								}}
							>
								<Rating value={review.score / 20} readOnly fractions={10} />
								{review.title && (
									<Text fw={700} size="lg" style={{ margin: 0 }}>
										{review.title}
									</Text>
								)}
							</div>
							<Spoiler
								maxHeight={100}
								showLabel="Show more"
								hideLabel="Show less"
							>
								<Text>{review.text}</Text>
							</Spoiler>
						</div>
					</Paper>
				</Carousel.Slide>
			))}
		</Carousel>
	)
}

const GuidePage = ({ guide, version, initialReviews }: GuidePageProps) => {
	// const { isFallback } = useRouter()

	const backgroundImage = guide?.jsonProfile?.coverImageUploadId
		? getUploadThumbPath(guide?.jsonProfile?.coverImageUploadId, 'large')
		: getDefaultImage(version)

	// const mainTitle = `Discover the Expertise of ${guide.companyName}`
	const seoTitle = `${capitalizeWords(
		guide.companyName,
	)} - Reviews, Ratings, & Trips`

	const seo = {
		title: `${seoTitle} - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description: `Discover guided expeditions with ${guide.companyName}: trips, ratings & reviews, contact details, languages, & social channels.`,
		canonical: getGuideProfileUrl(version, sluggify(guide.companyName)),
		coverImageUrl: backgroundImage,
		siteName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		// keywords: article?.liveJson?.tags ?? [],
		authorName:
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks',
		datePublished: guide?.updatedAt ?? '',
		images: [backgroundImage],
	}

	const {
		phoneNumber,
		email,
		website,
		mainLocation,
		businessWhatsappNumber,
		tripAdvisorReviewURL,
		googleMapsReviewURL,
		trustPilotReviewURL,
		spokenLanguages,
		aboutInfoBlurb,
		youtubeVideo,
		youtubeChannel,
		instagramHandle,
		facebookHandle,
		tiktokHandle,
		certificationsBlurb,
		logoUploadId,
		coverImageUploadId,
		yearStarted,
		discounts,
		// ...rest
	} = guide?.jsonProfile ?? {}

	const youtubeId = React.useMemo(() => {
		if (youtubeVideo !== undefined) {
			const regex = /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/

			const match = youtubeVideo.match(regex)
			return match ? match[1] : null
		}
	}, [youtubeVideo])

	const titleMarginTop = 'xl'
	const titleMarginBottom = 'md'

	// defensive: only trips of this site's variant (issue #114) — a TREK
	// offering on PEAKS would link to /trek/… which 404s there
	const siteTrekOrClimb =
		version === Types.VERSION.TREKKING
			? Types.App.DataTypes.TrekOrClimb.TREK
			: Types.App.DataTypes.TrekOrClimb.CLIMB
	const guideTrips = (guide?.guideTrips ?? []).filter(
		(guideTrip) =>
			!!guideTrip?.trip &&
			guideTrip.trip?.jsonData?.trekOrClimb === siteTrekOrClimb,
	)

	const priceRange = React.useMemo(() => {
		if (guideTrips.length === 0) {
			return undefined
		}

		// Extract all prices from the guideTrips array
		const prices = guideTrips.map((trip) => trip.price)

		// Calculate the lowest and highest prices
		const lowestPrice = Math.min(...prices)
		const highestPrice = Math.max(...prices)

		return {
			lowestPrice,
			highestPrice,
		}
	}, [guideTrips])

	const countriesActiveIn = React.useMemo(() => {
		const countryCount: { [key: string]: number } = {}

		guideTrips.forEach((guideTrip) => {
			guideTrip.trip?.jsonData?.countries.forEach((country: string) => {
				countryCount[country] = (countryCount[country] || 0) + 1
			})
		})

		return countryCount
	}, [guideTrips])

	const climbingWithGuidePhotos = (guide?.guideUploads ?? []).filter(
		(upload) =>
			upload.resourceType === Types.App.DataTypes.ResourceType.UNIQUE_TRIP,
	)

	const [lightboxIndex, setLightboxIndex] = React.useState(-1)

	const lightboxThumbClicked = (idClicked: number) => {
		const imgKey = climbingWithGuidePhotos.findIndex(
			({ id }) => id === idClicked,
		)
		setLightboxIndex(imgKey)
	}

	const guideTripMultiPeaks = guide.guideTripMultiPeaks ?? []

	const hasNoTrips = guideTrips.length === 0 && guideTripMultiPeaks.length === 0

	const isPeruExpeditions = guide.id === 59

	const contactThings = []
	const ctaThings = []

	const iconStyle = {
		strokeWidth: '1.25',
		// width: 16,
		height: 20,
	}

	if (mainLocation) {
		contactThings.push({
			icon: <IconMapPin {...iconStyle} />,
			value: mainLocation,
		})
	}

	if (yearStarted) {
		contactThings.push({
			icon: null,
			value: (
				<span
					style={{ color: '#888', fontStyle: 'italic', fontSize: '0.95em' }}
				>
					Started guiding in {yearStarted} -{' '}
					{dayjs().diff(dayjs(yearStarted, 'YYYY'), 'years')} years ago.
				</span>
			),
		})
	}

	if (website) {
		ctaThings.push({
			icon: <IconWorldWww {...iconStyle} />,
			value: (
				<a
					href={`/api/link?pGuideCompanyIdProfileClicked=${guide.id}`}
					target="_blank"
					rel="noopener nofollow"
				>
					{website}
				</a>
			),
		})
	}

	if (email) {
		ctaThings.push({
			icon: <IconMail {...iconStyle} />,
			value: <a href={`mailto:${email}`}>{email}</a>,
		})
	}

	if (phoneNumber) {
		ctaThings.push({
			icon: <IconPhone {...iconStyle} />,
			value: <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>,
		})
	}

	if (businessWhatsappNumber) {
		const formattedNumber = businessWhatsappNumber.replace(/[^0-9]/g, '')
		const url = `https://wa.me/${formattedNumber}`

		const handleWhatsAppClick = () => {
			window.open(url, '_blank')
		}

		ctaThings.push({
			icon: <IconBrandWhatsapp {...iconStyle} />,
			value: (
				<Button
					onClick={handleWhatsAppClick}
					color="green"
					variant={'filled'}
					size="xs"
					p="xs"
				>
					Contact on WhatsApp
				</Button>
			),
		})
	}

	const socialThings = []

	if (instagramHandle) {
		socialThings.push({
			icon: <IconBrandInstagram {...iconStyle} />,
			value: (
				<a
					href={`https://instagram.com/${instagramHandle}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					{instagramHandle}
				</a>
			),
		})
	}

	if (facebookHandle) {
		socialThings.push({
			icon: <IconBrandFacebook {...iconStyle} />,
			value: (
				<a
					href={`https://facebook.com/${facebookHandle}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					{facebookHandle}
				</a>
			),
		})
	}

	if (tiktokHandle) {
		socialThings.push({
			icon: <IconBrandTiktok {...iconStyle} />,
			value: (
				<a
					href={`https://tiktok.com/@${tiktokHandle}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					{tiktokHandle}
				</a>
			),
		})
	}

	if (youtubeChannel) {
		const usernameMatch = youtubeChannel.match(/\/@(.+)$/)
		const username = usernameMatch ? usernameMatch[1] : youtubeChannel // Fallback to full URL if no match
		// stored values may lack a scheme (e.g. "www.youtube.com/@…"), which
		// would resolve as a relative /guides/… link (issue #115)
		const channelURL = ensureHttpUrl(youtubeChannel)

		socialThings.push({
			icon: <IconBrandYoutube {...iconStyle} />,
			value: (
				<a href={channelURL} target="_blank" rel="noopener noreferrer">
					@{username} {/* Display the username instead of the full URL */}
				</a>
			),
		})
	}

	const renderReviewScore = (
		score?: number | null,
		count?: number | null,
		label = '',
	) => {
		if (!score) return null
		return (
			<Badge
				color="green"
				variant="light"
				size="lg"
				style={{ marginRight: '8px' }}
			>
				{label && <span style={{ marginRight: '4px' }}>{label}:</span>}
				{score / 20}/5 ({count} reviews)
			</Badge>
		)
	}

	const jsonLd = {
		'@context': 'https://schema.org',
		'@type': 'LocalBusiness',
		name: guide.companyName,
		// jobTitle: 'Mountain Guide',
		// worksFor: {
		// 	'@type': 'Organization',
		// 	name: guide.companyName,
		// },
		url: seo.canonical,
		...(!!guide?.companyReviewData?.totalReviewAverage &&
			guide?.companyReviewData?.totalReviewCount && {
				aggregateRating: {
					'@type': 'AggregateRating',
					ratingValue: (guide.companyReviewData.totalReviewAverage / 100) * 5,
					reviewCount: guide.companyReviewData.totalReviewCount.toString(),
				},
			}),
	}

	const isShowingCertifications =
		!!certificationsBlurb || (guide?.certificates ?? []).length > 0
	const isShowingReviewData =
		guide.companyReviewData &&
		(guide.companyReviewData?.totalReviewCount ?? 0) > 0
	const isShowingReviewSummary = !!guide?.aiReviewSummary

	return (
		<>
			<Head>
				<title>{seo.title}</title>
			</Head>
			<NextSeo
				title={seo.title}
				description={seo.description}
				canonical={seo.canonical}
				openGraph={{
					url: seo.canonical,
					title: seo.title,
					description: seo.description,
					images: seo.coverImageUrl
						? [
								{
									url: seo.coverImageUrl,
									// width: +image.dimensionsLarge.split('x')[0],
									// height: +image.dimensionsLarge.split('x')[1],
									alt: guide.companyName,
									type: 'image/jpeg',
								},
						  ]
						: [],
					siteName: seo.siteName,
					type: 'website',
					locale: 'en_US',
				}}
			/>
			<Script
				id="json-ld"
				type="application/ld+json"
				dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
			/>

			<PageTemplate isFullWidth={false} isWithoutTopMargin={true}>
				<div id="guide-slug-page">
					{!guide.isOwned && !isPeruExpeditions && (
						<Alert title="Is this your company?" mb="xl">
							Take ownership of this listing to manage your profile and connect
							with {version === Types.VERSION.PEAKS ? 'climbers' : 'trekkers'}.
							Contact us at{' '}
							<strong>
								<EmailAddress version={version} />
							</strong>{' '}
							to get started.
						</Alert>
					)}
					{coverImageUploadId !== undefined && (
						<>
							<div
								style={{
									position: 'relative',
									height: '580px',
									width: '100vw',
									left: '50%',
									transform: 'translateX(-50%)',
									overflow: 'hidden',
									marginBottom: '-480px',
									zIndex: -1,
								}}
							>
								<Image
									src={getUploadThumbPath(coverImageUploadId, 'large')}
									alt={guide.companyName + ' cover image'}
									layout="fill"
									objectFit="cover"
									style={{ opacity: 0.7, borderRadius: 0 }}
								/>
								<div
									style={{
										position: 'absolute',
										bottom: 0,
										left: 0,
										width: '100%',
										height: '120px', // Adjust height of the fade
										background:
											'linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)',
									}}
								/>
							</div>
						</>
					)}
					<Paper
						shadow="md"
						p="md"
						// withBorder={true}
						radius="lg"
						style={{
							maxWidth: '960px',
							margin: '0 auto',
							backgroundColor: 'rgba(255, 255, 255, 0.9)',
						}}
						mb={'xl'}
					>
						{isPeruExpeditions && (
							<Alert mt="lg" mb="lg" color="orange" title="Company renamed">
								Note: This company was rebranded to{' '}
								<strong>Grupo Alpamayo Tours</strong>. Please check out their
								profile and trips{' '}
								<Link href="/guides/grupo-alpamayo-tours">here</Link> instead.
							</Alert>
						)}
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '32px',
								marginBottom: 24,
							}}
						>
							{logoUploadId !== undefined && (
								<div
									style={{
										display: 'flex',
										alignItems: 'center',
										height: '100%',
									}}
								>
									<Image
										src={getUploadThumbPath(logoUploadId, 'medium')}
										alt={`${guide.companyName} logo`}
										width={180}
										height={120}
										style={{
											maxHeight: '120px',
											maxWidth: '180px',
											width: 'auto',
											height: 'auto',
											borderRadius: 8,
											objectFit: 'contain',
											display: 'block',
											background: '#fff',
											padding: '6px',
										}}
									/>
								</div>
							)}
							<div>
								<Title
									order={1}
									style={{
										margin: 0,
										flexGrow: 1,
										display: 'flex',
										alignItems: 'center',
										gap: '8px',
									}}
									mt={titleMarginTop}
									mb={0}
								>
									{capitalizeWords(guide.companyName)}
									{guide.isPremium ? (
										<span
											style={{
												color: '#B8860B',
												fontSize: '0.8em',
												display: 'inline-flex',
												alignItems: 'center',
											}}
											title="Recommended Partner"
										>
											★
										</span>
									) : null}
								</Title>
								<Title
									order={2}
									style={{
										margin: 0,
									}}
									mt={0}
									mb={titleMarginBottom}
								>
									Reviews, Ratings, & Trips
								</Title>
								{contactThings.length > 0 && (
									<Box
										mt="sm"
										style={{
											display: 'flex',
											flexWrap: 'wrap',
											gap: '16px',
											rowGap: '8px',
											maxWidth: '100%',
										}}
									>
										{contactThings.map((item, idxContactDetail) => (
											<div
												key={idxContactDetail}
												style={{
													display: 'inline-flex',
													gap: '4px',
													alignItems: 'center',
													maxWidth: '100%',
												}}
											>
												<div
													style={{
														flexShrink: 0,
														display: 'flex',
														alignItems: 'center',
													}}
												>
													{item.icon}
												</div>
												<div
													style={{
														flexGrow: 1,
														whiteSpace: 'nowrap',
														overflow: 'hidden',
														textOverflow: 'ellipsis',
													}}
												>
													{item.value}
												</div>
											</div>
										))}
									</Box>
								)}
							</div>
						</div>
						{(!!youtubeId || !!aboutInfoBlurb) && (
							<div className="guide-profile-golden-row">
								{!!youtubeId && (
									<div className="guide-profile-golden-col guide-profile-midline-trips">
										<Video
											id={youtubeId}
											title={`${guide.companyName} video`}
											autoplay={true}
											rounded={true}
										/>
									</div>
								)}
								{!!aboutInfoBlurb && (
									<div
										className={`guide-profile-golden-col ${
											youtubeId ? 'guide-profile-midline-carousel' : ''
										}`}
										style={{ flex: youtubeId ? undefined : '1 1 100%' }}
									>
										<Spoiler
											maxHeight={196}
											showLabel="Show more"
											hideLabel=""
											styles={{
												control: {
													display: 'block',
													margin: '16px auto 0 auto',
													textAlign: 'center',
												},
											}}
										>
											<Blockquote
												cite={guide.companyName}
												style={{ marginTop: 0, paddingTop: 0 }}
											>
												{aboutInfoBlurb}
											</Blockquote>
										</Spoiler>
									</div>
								)}
							</div>
						)}
						<Group
							justify="apart"
							gap={0}
							grow
							wrap="nowrap"
							className="guide-profile-golden-row guide-profile-midline-row"
							mb="xl"
						>
							<Box className="guide-profile-golden-col guide-profile-midline-carousel">
								<TopReviews guide={guide} initialReviews={initialReviews} />
							</Box>
							<Box className="guide-profile-golden-col guide-profile-midline-trips">
								<Paper p="md" withBorder>
									{guideTrips.length === 0 ? (
										<Text color="dimmed" size="sm">
											No trips listed yet.
										</Text>
									) : (
										<Stack gap="xs" style={{ width: '100%' }}>
											{guideTrips.slice(0, 3).map((guideTrip, idx, arr) => {
												const countryCode =
													guideTrip.trip?.jsonData?.countries?.[0]
												const countryName = countryCode
													? new Intl.DisplayNames(['en'], {
															type: 'region',
													  }).of(countryCode)
													: ''
												const flagUrl = countryCode
													? getFlagURL(countryCode)
													: ''
												const tripSlug = guideTrip.trip?.slug
												return (
													<div key={idx}>
														<a
															href={`#trip-${tripSlug}`}
															style={{
																textDecoration: 'none',
																color: 'inherit',
																display: 'block',
															}}
														>
															<Group gap="xs" align="center" wrap="nowrap">
																{guideTrip.trip?.uploads?.[0]?.id && (
																	<Image
																		src={getUploadThumbPath(
																			guideTrip.trip.uploads[0].id,
																			'small',
																		)}
																		alt={guideTrip.trip?.name + ' image'}
																		width={36}
																		height={36}
																		style={{
																			borderRadius: 6,
																			objectFit: 'cover',
																		}}
																	/>
																)}
																<Box style={{ minWidth: 0 }}>
																	<Text
																		size="sm"
																		fw={600}
																		style={{
																			whiteSpace: 'nowrap',
																			overflow: 'hidden',
																			textOverflow: 'ellipsis',
																			marginRight: 2,
																		}}
																	>
																		{guideTrip.trip?.name}
																	</Text>
																	<Group gap={8} align="center" mt={2}>
																		<Text size="xs" color="dimmed">
																			{guideTrip.trip?.jsonData?.highLevelInfo?.climb?.maxElevation?.toLocaleString()}
																			m
																		</Text>
																		{countryCode && flagUrl && (
																			<span
																				style={{
																					display: 'inline-flex',
																					alignItems: 'center',
																					opacity: 0.8,
																					fontSize: 12,
																					marginLeft: 2,
																				}}
																			>
																				<Image
																					src={flagUrl}
																					alt={countryCode}
																					width={16}
																					height={12}
																					style={{
																						display: 'inline',
																						verticalAlign: 'middle',
																						marginRight: 3,
																						borderRadius: 2,
																					}}
																				/>
																				<span>{countryName}</span>
																			</span>
																		)}
																		<Text
																			size="xs"
																			color="dimmed"
																			style={{ marginLeft: 6 }}
																		>
																			{guideTrip.price?.toLocaleString()
																				? `$${guideTrip.price?.toLocaleString()}`
																				: ''}
																		</Text>
																	</Group>
																</Box>
															</Group>
														</a>
														{idx < arr.length - 1 && (
															<Divider mt="xs" variant="dotted" />
														)}
													</div>
												)
											})}
											{guideTrips.length > 3 && (
												<>
													<Divider variant="dotted" />
													<Center>
														<a href="#trips-section">
															All {guideTrips.length} trips
														</a>
													</Center>
												</>
											)}
										</Stack>
									)}
								</Paper>
							</Box>
						</Group>
						<Group
							justify="apart"
							gap={0}
							grow
							wrap="nowrap"
							className="guide-profile-golden-row guide-profile-bottom-row"
							mb="xl"
						>
							<Box className="guide-profile-golden-col guide-profile-bottom-score">
								{guide.companyReviewData?.totalReviewCount &&
									guide.companyReviewData?.totalReviewAverage && (
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<Link
												href="#reviews"
												style={{ textDecoration: 'none', textAlign: 'center' }}
											>
												<div
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													<Rating
														value={
															guide.companyReviewData.totalReviewAverage / 20
														}
														readOnly
														fractions={10}
														size="xl"
														mb="md"
														styles={{
															root: {
																transform: 'scale(1.4)',
																transformOrigin: 'center',
																pointerEvents: 'none',
															},
														}}
													/>
												</div>
												<Text size="xl" fw={500} style={{ color: 'black' }}>
													Rated{' '}
													{(
														guide.companyReviewData.totalReviewAverage / 20
													).toFixed(1)}{' '}
													/ 5 (
													<span style={{ color: ACCENT_COLOUR }}>
														{guide.companyReviewData.totalReviewCount}{' '}
														{pluralize(
															'reviewer',
															guide.companyReviewData.totalReviewCount ?? 0,
														)}
													</span>
													)
												</Text>
											</Link>
										</div>
									)}
							</Box>
							<Box className="guide-profile-golden-col guide-profile-bottom-ctas">
								<div
									style={{
										display: 'flex',
										flexDirection: 'column',
										gap: '12px',
									}}
								>
									{ctaThings.map((item, idx) => (
										<div
											key={idx}
											style={{
												display: 'flex',
												alignItems: 'center',
												gap: '8px',
											}}
										>
											{item.icon}
											{item.value}
										</div>
									))}
								</div>
							</Box>
						</Group>
						<div>
							{(spokenLanguages ?? []).length > 0 && (
								<Box
									mt="sm"
									style={{
										display: 'flex',
										alignItems: 'flex-start',
									}}
								>
									<div
										style={{
											flex: '0 0 auto',
											marginRight: '16px',
											fontSize: 'smaller',
										}}
									>
										<div>Speaks:</div>
									</div>
									<div
										style={{
											flex: '1 1 auto',
											display: 'flex',
											flexWrap: 'wrap',
											gap: '8px',
										}}
									>
										{spokenLanguages?.map((language, key) => (
											<Badge key={key} radius="md" variant="outline">
												{language}
											</Badge>
										))}
									</div>
								</Box>
							)}
							{(Object.values(countriesActiveIn) ?? []).length > 0 && (
								<Box
									mt="sm"
									style={{
										display: 'flex',
										alignItems: 'flex-start',
									}}
								>
									<div
										style={{
											flex: '0 0 auto',
											marginRight: '16px',
											fontSize: 'smaller',
										}}
									>
										<div>Trips in:</div>
									</div>
									<div
										style={{
											flex: '1 1 auto',
											display: 'flex',
											flexWrap: 'wrap',
											gap: '8px',
										}}
									>
										{Object.entries(countriesActiveIn).map(
											([countryCode, tripCount]) => (
												<CountryBadge
													key={'country' + countryCode}
													isoCode={countryCode}
													// append={` - ${tripCount} ${pluralize(
													// 	'trip',
													// 	tripCount,
													// )}`}
												/>
											),
										)}
									</div>
								</Box>
							)}
							{socialThings.length > 0 && (
								<Box
									mt="sm"
									style={{
										display: 'flex',
										flexWrap: 'wrap',
										gap: '16px',
										rowGap: '8px',
										maxWidth: '100%',
									}}
								>
									{socialThings.map((item, idxSocialThing) => (
										<div
											key={idxSocialThing}
											style={{
												display: 'inline-flex',
												gap: '4px',
												alignItems: 'center',
												maxWidth: '100%',
											}}
										>
											<div
												style={{
													flexShrink: 0,
													display: 'flex',
													alignItems: 'center',
												}}
											>
												{item.icon}
											</div>
											<div
												style={{
													flexGrow: 1,
													whiteSpace: 'nowrap',
													overflow: 'hidden',
													textOverflow: 'ellipsis',
												}}
											>
												{item.value}
											</div>
										</div>
									))}
								</Box>
							)}
						</div>
						<Tabs
							variant="outline"
							defaultValue="trips"
							keepMountedMode="display-none"
							mt="md"
							mb="xl"
						>
							<Tabs.List>
								<Tabs.Tab value="trips">Trips</Tabs.Tab>
								{isShowingReviewData && (
									<Tabs.Tab value="reviews">Reviews</Tabs.Tab>
								)}
								{isShowingReviewSummary && (
									<Tabs.Tab value="review-summary">Review Summary</Tabs.Tab>
								)}
								{isShowingCertifications && (
									<Tabs.Tab value="certifications">Certifications</Tabs.Tab>
								)}
							</Tabs.List>
							<Tabs.Panel value="trips" pt="xs">
								{guideTrips.length > 0 && (
									<>
										<Title
											order={2}
											mt={titleMarginTop}
											mb={titleMarginBottom}
											id="trips-section"
										>
											Trips offered by {guide.companyName}
										</Title>
										<p>
											{guide.companyName} has{' '}
											<strong>
												{guideTrips.length}{' '}
												{pluralize('trip', guideTrips.length)}
											</strong>
											. Ranging from{' '}
											<u>${priceRange?.lowestPrice.toLocaleString()}</u> to{' '}
											<u>${priceRange?.highestPrice.toLocaleString()}</u>.
										</p>
										{!!discounts && (
											<Alert
												mt="md"
												mb="lg"
												title={`${guide.companyName} has these discounts available for their trips:`}
												icon={<IconPigMoney />}
											>
												{discounts}
											</Alert>
										)}
										<div>
											{guideTrips.map((guideTrip, idxGuidedTrip) => {
												const tripDetails = []

												if (guideTrip.jsonDetails?.includesGear) {
													tripDetails.push(`🎒 Gear Included`)
												}

												if (guideTrip.jsonDetails?.includesPorters) {
													tripDetails.push(`👥 Porters Included`)
												}

												if (
													(guideTrip.jsonDetails?.typicalGroupSize ?? [])
														.length > 0
												) {
													tripDetails.push(
														`👥 Group Size: ${guideTrip.jsonDetails?.typicalGroupSize?.join(
															'-',
														)}`,
													)
												}

												if (
													(guideTrip.jsonDetails?.lengtOfTripInDays ?? [])
														.length > 0
												) {
													tripDetails.push(
														`⏳ ${guideTrip.jsonDetails?.lengtOfTripInDays?.join(
															'-',
														)} days`,
													)
												}

												if (
													(guideTrip.jsonDetails?.climberToGuideRatio ?? [])
														.length > 0
												) {
													tripDetails.push(
														`🧑‍🤝‍🧑 Climber to guide ratio: 1 Guide to every ${guideTrip.jsonDetails?.climberToGuideRatio?.join(
															'-',
														)} ${pluralize(
															'client',
															(guideTrip.jsonDetails?.climberToGuideRatio ?? [])
																.length,
														)}`,
													)
												}

												return (
													<div
														key={idxGuidedTrip}
														id={`trip-${guideTrip.trip?.slug}`}
													>
														<Box style={{ display: 'flex' }}>
															<Box
																style={{ width: '180px', overflow: 'hidden' }}
															>
																{/* Placeholder for the image */}
																<Image
																	src={getUploadThumbPath(
																		guideTrip.trip?.uploads?.[0].id as number,
																		'medium',
																	)} // Adjust the image source as needed
																	alt={`${guideTrip.trip?.name} image`}
																	width={180}
																	height={0}
																	layout="responsive"
																	style={{
																		objectFit: 'cover',
																		borderRadius: '8px',
																	}}
																/>
															</Box>
															<Box style={{ flex: 1, paddingLeft: '16px' }}>
																{/* Content for the right column */}
																{/* Replace with your desired content */}
																<Title
																	order={3}
																	style={{
																		display: 'flex',
																		gap: '4px',
																		alignItems: 'center',
																	}}
																	mb="xs"
																	id={`trip-${guideTrip.trip?.slug}-title`}
																>
																	<Link
																		href={getTrekRoute(
																			guideTrip.trip?.jsonData
																				.trekOrClimb as Types.App.DataTypes.TrekOrClimb,
																			guideTrip.trip?.jsonData
																				.continent as string,
																			guideTrip.trip?.jsonData
																				.countries as string[],
																			guideTrip.trip?.slug as string,
																		)}
																	>
																		{guideTrip.trip?.name}
																	</Link>

																	<Badge variant={'filled'}>
																		${guideTrip.price?.toLocaleString()}
																	</Badge>
																</Title>
																{!!guideTrip.trip?.jsonData.climbDifficulty
																	?.simple && (
																	<Text mb="xs">
																		🧗 Difficulty:{'  '}
																		{formatDifficulty(
																			guideTrip.trip?.jsonData.climbDifficulty
																				?.simple as number[],
																			'shortDescription',
																		)}
																	</Text>
																)}
																<Text mb="xs">
																	🏔️ Height:{'  '}
																	{guideTrip.trip?.jsonData?.highLevelInfo?.climb?.maxElevation?.toLocaleString()}
																	m
																</Text>
																<Box
																	style={{
																		display: 'flex',
																		flexDirection: 'row',
																		gap: '8px',
																		alignItems: 'center',
																	}}
																	mb="xs"
																>
																	<div>📍 Where: </div>
																	<div
																		style={{
																			flex: '1 1 auto',
																			display: 'flex',
																			flexWrap: 'wrap',
																			gap: '8px',
																		}}
																	>
																		{guideTrip.trip?.jsonData.countries.map(
																			(isoCode: string, idxCountry: number) => (
																				<Link
																					href={getCountryRoute(
																						guideTrip.trip?.jsonData
																							.trekOrClimb as Types.App.DataTypes.TrekOrClimb,
																						guideTrip.trip?.jsonData
																							.continent as string,
																						isoCode,
																					)}
																					key={idxCountry}
																				>
																					<CountryBadge isoCode={isoCode} />
																				</Link>
																			),
																		)}
																	</div>
																</Box>

																{tripDetails.length > 0 && (
																	<>
																		<Title order={4} mb="md" mt="md">
																			What {guide.companyName}&apos;s expedition
																			looks like:
																		</Title>

																		<Text>
																			{tripDetails.map((detail, idxDetail) => (
																				<li key={idxDetail}>{detail}</li>
																			))}
																		</Text>
																	</>
																)}
																{(guideTrip.jsonDetails?.videos ?? []).length >
																	0 && (
																	<>
																		<Title order={4} mb="md" mt="md">
																			Climbing {guideTrip.trip?.name} with{' '}
																			{guide.companyName}:
																		</Title>
																		<div
																			style={{
																				display: 'grid',
																				gridTemplateColumns: 'repeat(2, 1fr)', // Two columns
																				gap: '12px', // Adjust the gap as needed
																			}}
																		>
																			{guideTrip.jsonDetails?.videos?.map(
																				(videoURL, idxVideo) => (
																					<div key={idxVideo}>
																						<Video
																							id={
																								extractYoutubeId(
																									videoURL,
																								) as string
																							}
																							title={`Video ${idxVideo + 1}`}
																						/>
																					</div>
																				),
																			)}
																		</div>
																	</>
																)}
																<div>
																	View on guides site:{' '}
																	<a
																		href={`/api/link?pGuideTripIdClicked=${guideTrip.id}`}
																		target="_blank"
																		rel="nofollow noreferrer ugc"
																		onMouseDown={() => {
																			AnalyitcsUtil.linkClickEvent()
																		}}
																		style={{
																			display: 'inline-flex',
																			alignItems: 'center',
																			backgroundColor: 'none',
																		}}
																	>
																		<span
																			style={{
																				display: 'inline-block',
																				// width: '150px',
																				overflow: 'hidden',
																				textOverflow: 'ellipsis',
																				whiteSpace: 'nowrap',
																			}}
																		>
																			www.
																			{guide.jsonProfile.website
																				?.replace('https://', '')
																				.replace('www.', '')}
																		</span>
																		<IconExternalLink
																			size={14}
																			style={{ marginLeft: '4px' }}
																		/>
																	</a>
																</div>
															</Box>
														</Box>
														{idxGuidedTrip !== guideTrips.length - 1 && (
															<Divider mt="sm" mb="sm" />
														)}
													</div>
												)
											})}
										</div>
									</>
								)}
								{guideTripMultiPeaks.length > 0 && (
									<>
										<Title order={2} mt={titleMarginTop} mb={titleMarginBottom}>
											Combined trips
										</Title>
										<p>
											{guide.companyName} also offers these trips where you can
											climb multiple mountains on one trip.
										</p>
										{(guide.guideTripMultiPeaks ?? []).map(
											(guideTripMultiPeak, key) => {
												const titlesJoined = formatTripNames(
													guideTripMultiPeak.trips?.map((trip) => trip.name),
												)

												return (
													<Paper
														key={key}
														shadow="md"
														radius="md"
														p="md"
														mt="lg"
														withBorder
													>
														<Title order={3} mb="md">
															{titlesJoined}{' '}
															<Badge size="xl" variant={'filled'}>
																<FormatPrice
																	price={guideTripMultiPeak.price}
																	currency={guideTripMultiPeak.currency}
																/>
															</Badge>
														</Title>
														<p>
															Full information{' '}
															<a
																// href={guideTripMultiPeak.webLink}

																href={`/api/link?pGuideTripMultiPeakIdClicked=${guideTripMultiPeak.id}`}
																// this warning seems incorrect, noopener secures it

																target="_blank"
																rel="noopener nofollow"
															>
																here
																<IconExternalLink
																	// icon="external-link"
																	size={14}
																	style={{
																		marginLeft: '4px',
																		verticalAlign: 'middle',
																	}}
																/>
															</a>
														</p>
														<div
															className="trek-card-container"
															style={{
																gridTemplateColumns:
																	'repeat(3, minmax(200px, 2fr))',
															}}
														>
															{(guideTripMultiPeak.trips ?? [])
																// .filter((trip) => trip !== undefined)
																.map((guideTrip, key) => (
																	<TrekCard
																		tripCardProps={tripPageTripToTripCard(
																			// @ts-ignore
																			guideTrip,
																		)}
																		key={key}
																	/>
																))}
														</div>
													</Paper>
												)
											},
										)}
									</>
								)}
								{hasNoTrips && (
									<Alert mt="lg" mb="lg">
										We don&apos;t have any trips listed for this guide.
									</Alert>
								)}
							</Tabs.Panel>
							{isShowingReviewData && guide.companyReviewData && (
								<Tabs.Panel value="reviews" pt="xs">
									<Title
										order={2}
										mt={titleMarginTop}
										mb={titleMarginBottom}
										id="reviews"
									>
										{guide.companyName} Reviews & Ratings
									</Title>
									<Text mb="md">How well reviewed is {guide.companyName}?</Text>
									<Text mb="md">
										{guide.companyName} has a{' '}
										{Math.round(
											((guide.companyReviewData?.totalReviewAverage ?? 0) /
												100) *
												100,
										)}
										% rating from{' '}
										{guide.companyReviewData?.totalReviewCount ?? 0}{' '}
										{pluralize(
											'reviewer',
											guide.companyReviewData?.totalReviewCount ?? 0,
										)}{' '}
										on independent review sites.
									</Text>

									<div style={{ overflowX: 'auto', maxWidth: '100%' }}>
										<Table mb="xl">
											<thead>
												<tr>
													<th style={{ minWidth: '120px' }}></th>
													<th style={{ minWidth: '120px' }}>Rating</th>
													<th
														style={{
															textAlign: 'right',
															paddingRight: '16px',
															minWidth: '80px',
														}}
													>
														Score
													</th>
													<th style={{ textAlign: 'right', minWidth: '80px' }}>
														Reviews
													</th>
												</tr>
											</thead>
											<tbody>
												{guide.companyReviewData.totalReviewAverage && (
													<ReviewRow
														name="Guided Peaks"
														average={guide.companyReviewData.totalReviewAverage}
														count={
															guide.companyReviewData.totalReviewCount ?? 0
														}
														isInternalScore={true}
													/>
												)}
												{guide.companyReviewData.taReviewAverage && (
													<ReviewRow
														name="TripAdvisor"
														url={tripAdvisorReviewURL}
														average={guide.companyReviewData.taReviewAverage}
														count={
															guide.companyReviewData.taReviewCount as number
														}
													/>
												)}
												{guide.companyReviewData.goReviewAverage && (
													<ReviewRow
														name="Google"
														url={googleMapsReviewURL}
														average={guide.companyReviewData.goReviewAverage}
														count={
															guide.companyReviewData.goReviewCount as number
														}
													/>
												)}
												{guide.companyReviewData.tpReviewAverage && (
													<ReviewRow
														name="TrustPilot"
														url={trustPilotReviewURL}
														average={guide.companyReviewData.tpReviewAverage}
														count={
															guide.companyReviewData.tpReviewCount as number
														}
													/>
												)}
											</tbody>
										</Table>
									</div>

									<ReviewsSection
										guide={guide}
										initialReviews={initialReviews}
									/>
								</Tabs.Panel>
							)}
							{isShowingReviewSummary && !!guide?.aiReviewSummary && (
								<Tabs.Panel value="review-summary" pt="xs">
									<Title order={2} mt={titleMarginTop} mb={titleMarginBottom}>
										Summary of {guide.companyName} reviews
									</Title>
									<div
										dangerouslySetInnerHTML={{
											__html: guide.aiReviewSummary,
										}}
									/>
								</Tabs.Panel>
							)}
							{isShowingCertifications && (
								<Tabs.Panel value="certifications" pt="xs">
									<Title order={2} mt={titleMarginTop} mb={titleMarginBottom}>
										{guide.companyName} Certifications
									</Title>
									{!!certificationsBlurb && (
										<>
											<p>{certificationsBlurb}</p>
										</>
									)}

									{(guide?.certificates ?? []).length > 0 && (
										<>
											<Title order={3} mb="md">
												{guide.companyName} has uploaded these certifications:
											</Title>

											<div
												style={{
													display: 'flex',
													gap: '8px',
													flexWrap: 'wrap',
												}}
											>
												{guide.certificates?.map((cert, idxCert) => (
													<Popover
														key={idxCert}
														position="bottom"
														withArrow
														shadow="md"
														arrowSize={16}
													>
														<Popover.Target>
															<div
																style={{
																	border: '1px solid silver',
																	borderRadius: '6px',
																	padding: '2px 6px',
																	display: 'flex',
																	alignItems: 'center',
																	gap: '8px',
																	cursor: 'zoom-in',
																}}
															>
																<IconCertificate
																	size={20}
																	style={{
																		verticalAlign: 'middle',
																		lineHeight: '100%',
																	}}
																/>
																<span>{cert.certificateName}</span>
															</div>
														</Popover.Target>
														<Popover.Dropdown>
															<MantineImage
																style={{
																	maxWidth: '80vw',
																	height: 'auto',
																	maxHeight: '80vw',
																}}
																src={getUploadThumbPath(cert.imageId, 'medium')}
																radius="md"
															/>
														</Popover.Dropdown>
													</Popover>
												))}
											</div>
										</>
									)}
								</Tabs.Panel>
							)}
						</Tabs>

						{climbingWithGuidePhotos.length > 0 && (
							<>
								<Title order={2} mt={titleMarginTop} mb={titleMarginBottom}>
									What{' '}
									{version === Types.VERSION.TREKKING
										? 'trekking'
										: 'mountaineering'}{' '}
									with {guide.companyName} looks like
								</Title>
								<Text>
									These pictures were uploaded by {guide.companyName} from
									expeditions they&apos;ve done.
								</Text>

								<ImageModal
									activeImageIndex={lightboxIndex}
									images={climbingWithGuidePhotos}
									changeActiveImageIndex={setLightboxIndex}
								/>
								<JustifiedGallery
									pictures={climbingWithGuidePhotos}
									size="small"
									altText={`${
										version === Types.VERSION.TREKKING
											? 'trekking'
											: 'mountaineering'
									} with ${guide.companyName}`}
									onImageClick={lightboxThumbClicked}
									// onImageClick={(id) => alert(`clicked ${id}`)}
								/>
							</>
						)}
						{/* {JSON.stringify(climbingWithGuidePhotos ?? [])} */}
						<Divider mt="xl" mb="xl" />
						<Title order={2} mt={titleMarginTop} mb={titleMarginBottom}>
							Can&apos;t Find the Right Guide?
						</Title>
						<Text>
							Our <Link href="/guides">Guide Directory</Link> has hundreds of
							guides around with world.
						</Text>
					</Paper>
				</div>
			</PageTemplate>
		</>
	)
}

interface GuidePageQuery extends ParsedUrlQuery {
	guideSlug?: string
}

export const getServerSideProps = withVersion(async (context) => {
	const { guideSlug } = context.params as GuidePageQuery

	if (!guideSlug) {
		// 404
		return {
			notFound: true,
		}
	}

	const guidePageData = await RedisUtil.Data.GuideProfile.get({
		version: context.version,
		slug: guideSlug,
	})

	if (!guidePageData) {
		return {
			notFound: true,
		}
	}

	// SSR the first page of reviews (issue #118): the reviews section and
	// top-reviews carousel otherwise mount client-side via useQuery, leaving
	// crawlers without JS almost no unique content
	const initialReviews = await ReviewsUtil.getGuideReviews({
		input: {
			guideId: guidePageData.guide.id,
			page: 1,
			limit: 10,
			provider: null,
		},
	})

	return {
		props: {
			guide: guidePageData.guide,
			version: guidePageData.version,
			initialReviews,
		},
	}
})

export default GuidePage
